import { IconType } from 'react-icons';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { IconButton } from 'app/agriculture/components/Button/Button';
import { ContentBoxSection } from './components/ContentBoxSection';
import { ContentBoxCollapse } from './components/ContentBoxCollapse';
import { ContentBoxHeading } from './components/ContentBoxHeading';
import { HTMLAttributes, useContext, ReactNode } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import './ContentBox.scss';
import { AsideStateContext } from 'app/agriculture/components/ContentBox/context/AsideStateContextProvider';
import { useFarmPath } from 'app/agriculture/router/useFarmPath';

interface ContentBoxProps {
    title: string;
    Icon?: IconType;
    className?: string;
    showOnMapButton?: ReactNode;
    variant?: 'sm' | 'md' | 'lg' | 'xl';
    children?: ReactNode;
    aside?: ReactNode;
    forceAsideOpen?: boolean;
    headerContent?: ReactNode;
}

function ContentBox({
    title,
    className,
    Icon,
    children,
    showOnMapButton,
    variant = 'sm',
    aside,
    forceAsideOpen = false,
    headerContent,
    ...divProps
}: ContentBoxProps & HTMLAttributes<HTMLDivElement>) {
    const { selectionService } = useContext(ServicesContext);
    const { isAsideOpen } = useContext(AsideStateContext);
    const navigate = useNavigate();
    const farmPath = useFarmPath();

    return (
        <div className="ContentBox__wrapper">
            <div
                {...divProps}
                className={classNames(
                    'ContentBox',
                    'display-over-map',
                    className,
                    `ContentBox--${variant}`,
                )}
            >
                <div className="ContentBox__header">
                    {Icon && <Icon className="ContentBox__icon" size={18} />}
                    <h5 className="ContentBox__title">{title}</h5>
                    <div className="ContentBox__header-content">{headerContent}</div>
                    {showOnMapButton ?? null}
                    <IconButton
                        className="ContentBox__close"
                        icon="close"
                        tooltip="Close"
                        onClick={() => {
                            selectionService.clearSelection();
                            navigate(farmPath('/'));
                        }}
                    />
                </div>
                <div className="ContentBox__content">{children}</div>
            </div>
            {aside == null || (!isAsideOpen && !forceAsideOpen) ? null : (
                <div className={classNames('ContentBox__aside', 'display-over-map')}>{aside}</div>
            )}
        </div>
    );
}

ContentBox.Section = ContentBoxSection;
ContentBox.Collapse = ContentBoxCollapse;
ContentBox.Heading = ContentBoxHeading;

export { ContentBox };
